/**
 * **What a person owns and owes, by hand** (Personal build step 3; Thomas, 2026-10-14, F372). Assets are added on the
 * decided [Asset Taxonomy](docs/Autho Me/10 Modules/Me/Asset Taxonomy.md), class → subclass → vehicle, through its
 * Type → Stake → Involvement steps; Held by, Purpose, Custody and Valuation are later steps and not asked here.
 * Liabilities are their own list — a loan, a credit card or something else — and never part of the asset tree. A value
 * is a dated row, so the history is kept and the newest is shown, in the currency it was given in and never converted.
 *
 * Pure: the tree, the rules for each step, and the sums a screen shows.
 */

/**
 * The decided tree. A subclass with no vehicles is itself the leaf; one with vehicles is chosen through them. The
 * taxonomy's Real Estate has a fourth level (Residential, Commercial, Land); it is folded into the subclass, so the
 * tree stays class → subclass → vehicle, as the decision names it. Keys only: the words are in `@autho/i18n`.
 */
export const ASSET_TREE = {
  public_markets: {
    stock: [],
    bond: [],
    fund: [],
    etf: [],
    reit: [],
    derivatives: [],
  },
  private_equity: {
    venture_capital: ['direct', 'secondary', 'fund'],
    growth_equity: ['direct', 'secondary', 'fund'],
    buyout: ['direct', 'fund'],
    private_company: ['operating_company', 'holding_company', 'sole_proprietorship'],
    equity_compensation: ['options', 'warrants', 'rsus', 'phantom_shares'],
  },
  private_credit: {
    direct_loan: [],
    shareholder_loan: [],
    convertible_note: [],
    personal_loan: [],
  },
  real_assets: {
    residential: ['primary_residence', 'rental_property', 'holiday_property', 'coop_share'],
    commercial: ['office', 'retail', 'industrial', 'hospitality'],
    land: ['development', 'agricultural', 'forestry', 'undeveloped'],
    infrastructure: ['energy', 'transportation', 'utilities'],
    natural_resources: ['direct_ownership', 'royalty_rights'],
  },
  commodities: {
    precious_metals: [],
    industrial_metals: [],
    energy: [],
    agriculture: [],
  },
  cash: {
    bank_deposit: [],
    physical_cash: [],
    receivable: [],
  },
  digital_assets: {
    cryptocurrency: [],
    stablecoins: [],
    tokens: [],
    nfts: [],
    staked_defi: [],
  },
  pensions_insurance: {
    occupational_pension: [],
    individual_pension: [],
    pension_capital_certificate: [],
    paid_up_policy: [],
    life_insurance: [],
    annuity: [],
  },
  collectibles: {
    art: [],
    watches_jewelry: [],
    vehicles_boats_aircraft: [],
    wine_spirits: [],
    other: [],
  },
  intellectual_property: {
    patents_technology: [],
    copyrights_media: [],
    trademarks_brands: [],
    domain_names: [],
    royalties: [],
  },
} as const satisfies Record<string, Record<string, readonly string[]>>;

export type AssetClass = keyof typeof ASSET_TREE;
export const ASSET_CLASSES = Object.keys(ASSET_TREE) as AssetClass[];

/** One leaf of the tree: what a person picks in the first step. */
export interface AssetType {
  /** `class.subclass` or `class.subclass.vehicle`: the value the database keeps and its vocabulary holds. */
  value: string;
  class: AssetClass;
  subclass: string;
  vehicle: string | null;
}

/** Every leaf, in the tree's order. */
export const ASSET_TYPES: readonly AssetType[] = ASSET_CLASSES.flatMap((cls) =>
  Object.entries(ASSET_TREE[cls] as Record<string, readonly string[]>).flatMap(
    ([subclass, vehicles]): AssetType[] =>
      vehicles.length === 0
        ? [{ value: `${cls}.${subclass}`, class: cls, subclass, vehicle: null }]
        : vehicles.map((vehicle) => ({
            value: `${cls}.${subclass}.${vehicle}`,
            class: cls,
            subclass,
            vehicle,
          })),
  ),
);

const BY_VALUE = new Map(ASSET_TYPES.map((type) => [type.value, type]));

export function assetType(value: unknown): AssetType | null {
  return typeof value === 'string' ? (BY_VALUE.get(value) ?? null) : null;
}

/**
 * **What the Stake step asks**, by the taxonomy's *Stake — input by type* table: shares for a company, units for a
 * fund, a face value for a loan, bond or note, a quantity for a commodity, a coin or an option, a share in per cent for
 * what is co-owned. The table names no input for Cash or Pensions & Insurance, whose value *is* the holding, so they
 * ask none (`none`); an NFT is a unique item (`unique`).
 */
export const STAKE_KINDS = [
  'shares',
  'units',
  'face',
  'quantity',
  'unique',
  'percent',
  'none',
] as const;
export type StakeKind = (typeof STAKE_KINDS)[number];

export function stakeKindOf(type: AssetType): StakeKind {
  switch (type.class) {
    case 'public_markets':
      return type.subclass === 'stock'
        ? 'shares'
        : type.subclass === 'bond'
          ? 'face'
          : type.subclass === 'derivatives'
            ? 'quantity'
            : 'units';
    case 'private_equity':
      return type.subclass === 'equity_compensation'
        ? 'quantity'
        : type.vehicle === 'fund'
          ? 'units'
          : 'shares';
    case 'private_credit':
      return 'face';
    case 'commodities':
      return 'quantity';
    case 'digital_assets':
      return type.subclass === 'nfts' ? 'unique' : 'quantity';
    case 'cash':
    case 'pensions_insurance':
      return 'none';
    default:
      // Real assets, collectibles and intellectual property: owned outright or co-owned, in per cent.
      return 'percent';
  }
}

/**
 * **Whether the Involvement step is shown**: *only for companies, real estate with operations, and IP*. A company is
 * a holding in shares under Private Equity; a fund, an option or a share of a fund is not. Every real asset may be
 * operated except a primary residence, which is *personal use with no involvement*.
 */
export function offersInvolvement(type: AssetType): boolean {
  if (type.class === 'private_equity') return stakeKindOf(type) === 'shares';
  if (type.class === 'real_assets') return type.vehicle !== 'primary_residence';
  return type.class === 'intellectual_property';
}

export const INVOLVEMENTS = ['passive', 'active', 'operating'] as const;
export type Involvement = (typeof INVOLVEMENTS)[number];

/** The stake as kept: each kind fills its own fields and leaves the rest null, as the database's checks require. */
export interface Stake {
  kind: StakeKind;
  /** Shares, units, a quantity or a face value; 1 for a unique item. Decimal text, as the database keeps it. */
  quantity: string | null;
  /** A share class, for shares. */
  shareClass: string | null;
  /** Shares outstanding, for shares, when known. */
  outstanding: string | null;
  /** Per cent: typed for a co-owned thing, typed or derived from outstanding for shares. */
  percent: string | null;
  /** Interest in per cent a year, for a face value. */
  rate: string | null;
  /** When a face value falls due, `YYYY-MM-DD`. */
  maturity: string | null;
}

export type HoldingError =
  | 'type_unknown'
  | 'stake_required'
  | 'stake_not_a_number'
  | 'stake_out_of_range'
  | 'stake_not_asked'
  | 'outstanding_below_shares'
  | 'share_class_too_long'
  | 'involvement_required'
  | 'involvement_not_offered'
  | 'name_required'
  | 'name_too_long'
  | 'text_too_long'
  | 'invalid_date'
  | 'date_in_future'
  | 'currency_unknown'
  | 'value_not_a_number'
  | 'kind_unknown';

export type Parsed<T> = { ok: true; value: T } | { ok: false; error: HoldingError };

const fail = <T>(error: HoldingError): Parsed<T> => ({ ok: false, error });

/** The most significant digits a decimal is kept with, and the most decimals: `numeric(28, 8)` in the database. */
const DECIMAL = /^(\d{1,20})(?:\.(\d{1,8}))?$/;

/**
 * A decimal as a person typed it in either language, `1 234,5` or `1234.5`; empty is null. Grouping spaces and the
 * no-break spaces `Intl` writes are taken out; one comma is a decimal comma. Never negative: what is owed is a
 * liability, not a negative asset.
 */
export function parseDecimal(input: unknown): Parsed<string | null> {
  if (input === null || input === undefined) return { ok: true, value: null };
  if (typeof input === 'number') {
    if (!Number.isFinite(input) || input < 0) return fail('stake_not_a_number');
    return parseDecimal(String(input));
  }
  if (typeof input !== 'string') return fail('stake_not_a_number');
  const text = input.replace(/\s/g, '').replace(',', '.');
  if (text === '') return { ok: true, value: null };
  const match = DECIMAL.exec(text);
  if (!match) return fail('stake_not_a_number');
  // Leading zeros and trailing decimal zeros are not part of a number: `007,50` is kept as `7.5`.
  const whole = String(match[1]).replace(/^0+(?=\d)/, '');
  const fraction = (match[2] ?? '').replace(/0+$/, '');
  return { ok: true, value: fraction === '' ? whole : `${whole}.${fraction}` };
}

const positive = (value: string) => Number(value) > 0;

function percentOf(input: unknown, required: boolean): Parsed<string | null> {
  const parsed = parseDecimal(typeof input === 'string' ? input.replace(/%\s*$/, '') : input);
  if (!parsed.ok) return parsed;
  if (parsed.value === null) return required ? fail('stake_required') : parsed;
  const value = Number(parsed.value);
  if (value <= 0 || value > 100) return fail('stake_out_of_range');
  return parsed;
}

function amountOf(input: unknown): Parsed<string> {
  const parsed = parseDecimal(input);
  if (!parsed.ok) return parsed;
  if (parsed.value === null) return fail('stake_required');
  return positive(parsed.value) ? { ok: true, value: parsed.value } : fail('stake_out_of_range');
}

/** A calendar day, `YYYY-MM-DD`, that exists. Empty is null. */
export function parseDay(input: unknown): Parsed<string | null> {
  if (input === null || input === undefined || input === '') return { ok: true, value: null };
  if (typeof input !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(input)) return fail('invalid_date');
  // A month 13 is no date at all, and a 30 February rolls into March: both are caught by reading the day back.
  const day = new Date(`${input}T00:00:00Z`);
  return !Number.isNaN(day.getTime()) && day.toISOString().slice(0, 10) === input
    ? { ok: true, value: input }
    : fail('invalid_date');
}

export interface StakeInput {
  quantity?: unknown;
  shareClass?: unknown;
  outstanding?: unknown;
  percent?: unknown;
  rate?: unknown;
  maturity?: unknown;
}

const EMPTY: Omit<Stake, 'kind'> = {
  quantity: null,
  shareClass: null,
  outstanding: null,
  percent: null,
  rate: null,
  maturity: null,
};

/** Any field the kind does not ask for, given anyway: refused rather than kept, so a stake says one thing. */
function asksOnly(input: StakeInput, asked: readonly (keyof StakeInput)[]): boolean {
  return (Object.keys(EMPTY) as (keyof StakeInput)[]).every(
    (field) =>
      asked.includes(field) ||
      input[field] === undefined ||
      input[field] === null ||
      input[field] === '',
  );
}

/**
 * **The Stake step**, for one type: what it asks, checked, and nothing it does not ask. For shares the per cent is
 * derived from shares outstanding when that is given, with four decimals, and otherwise may be typed; the band it
 * falls in is {@link stakeBand}'s.
 */
export function parseStakeFor(type: AssetType, input: StakeInput): Parsed<Stake> {
  const kind = stakeKindOf(type);
  switch (kind) {
    case 'shares': {
      if (!asksOnly(input, ['quantity', 'shareClass', 'outstanding', 'percent']))
        return fail('stake_not_asked');
      const shares = amountOf(input.quantity);
      if (!shares.ok) return shares;
      const shareClass = typeof input.shareClass === 'string' ? input.shareClass.trim() : '';
      if ([...shareClass].length > 40) return fail('share_class_too_long');
      const outstanding = parseDecimal(input.outstanding);
      if (!outstanding.ok) return outstanding;
      let percent: Parsed<string | null>;
      if (outstanding.value !== null) {
        if (Number(outstanding.value) < Number(shares.value))
          return fail('outstanding_below_shares');
        percent = {
          ok: true,
          value: ((Number(shares.value) / Number(outstanding.value)) * 100).toFixed(4),
        };
      } else percent = percentOf(input.percent, false);
      if (!percent.ok) return percent;
      return {
        ok: true,
        value: {
          ...EMPTY,
          kind,
          quantity: shares.value,
          shareClass: shareClass === '' ? null : shareClass,
          outstanding: outstanding.value,
          percent: percent.value,
        },
      };
    }
    case 'units':
    case 'quantity': {
      if (!asksOnly(input, ['quantity'])) return fail('stake_not_asked');
      const quantity = amountOf(input.quantity);
      if (!quantity.ok) return quantity;
      return { ok: true, value: { ...EMPTY, kind, quantity: quantity.value } };
    }
    case 'face': {
      if (!asksOnly(input, ['quantity', 'rate', 'maturity'])) return fail('stake_not_asked');
      const face = amountOf(input.quantity);
      if (!face.ok) return face;
      const rate = parseDecimal(input.rate);
      if (!rate.ok) return rate;
      if (rate.value !== null && Number(rate.value) > 100) return fail('stake_out_of_range');
      const maturity = parseDay(input.maturity);
      if (!maturity.ok) return maturity;
      return {
        ok: true,
        value: { ...EMPTY, kind, quantity: face.value, rate: rate.value, maturity: maturity.value },
      };
    }
    case 'percent': {
      if (!asksOnly(input, ['percent'])) return fail('stake_not_asked');
      const percent = percentOf(input.percent, true);
      if (!percent.ok) return percent;
      return { ok: true, value: { ...EMPTY, kind, percent: percent.value } };
    }
    case 'unique':
      if (!asksOnly(input, [])) return fail('stake_not_asked');
      return { ok: true, value: { ...EMPTY, kind, quantity: '1' } };
    default:
      if (!asksOnly(input, [])) return fail('stake_not_asked');
      return { ok: true, value: { ...EMPTY, kind } };
  }
}

/** **The Involvement step**: required where it is shown, refused where it is not. */
export function parseInvolvementFor(type: AssetType, input: unknown): Parsed<Involvement | null> {
  const given = input !== null && input !== undefined && input !== '';
  if (!offersInvolvement(type))
    return given ? fail('involvement_not_offered') : { ok: true, value: null };
  if (!given) return fail('involvement_required');
  return (INVOLVEMENTS as readonly unknown[]).includes(input)
    ? { ok: true, value: input as Involvement }
    : fail('involvement_required');
}

/**
 * **The stake thresholds** of the taxonomy: under 20 a financial interest, 20 to under 50 significant influence,
 * exactly 50 joint control, above 50 a majority, 100 wholly owned. Null where no per cent is known.
 */
export const STAKE_BANDS = ['financial', 'significant', 'joint', 'majority', 'wholly'] as const;
export type StakeBand = (typeof STAKE_BANDS)[number];

export function stakeBand(percent: string | null): StakeBand | null {
  if (percent === null) return null;
  const value = Number(percent);
  if (value >= 100) return 'wholly';
  if (value > 50) return 'majority';
  if (value === 50) return 'joint';
  return value >= 20 ? 'significant' : 'financial';
}

/** A short name a person gives the thing, required, at most 200 characters. */
export function parseName(input: unknown): Parsed<string> {
  const name = typeof input === 'string' ? input.trim() : '';
  if (name === '') return fail('name_required');
  return [...name].length > 200 ? fail('name_too_long') : { ok: true, value: name };
}

/** Optional free text: a lender, notes. Empty is null. */
export function parseText(input: unknown, max: number): Parsed<string | null> {
  const text = typeof input === 'string' ? input.trim() : '';
  if (text === '') return { ok: true, value: null };
  return [...text].length > max ? fail('text_too_long') : { ok: true, value: text };
}

/** **Liabilities are their own list** (Thomas, 2026-10-14): a loan, a credit card, or something else. */
export const LIABILITY_KINDS = ['loan', 'credit_card', 'other'] as const;
export type LiabilityKind = (typeof LIABILITY_KINDS)[number];

export function parseLiabilityKind(input: unknown): Parsed<LiabilityKind> {
  return (LIABILITY_KINDS as readonly unknown[]).includes(input)
    ? { ok: true, value: input as LiabilityKind }
    : fail('kind_unknown');
}

/** An ISO 4217 code `Intl` knows. Kept as given: a value in euros stays in euros (step 3, *never converted*). */
export function parseCurrency(input: unknown): Parsed<string> {
  const code = typeof input === 'string' ? input.trim().toUpperCase() : '';
  return /^[A-Z]{3}$/.test(code) && Intl.supportedValuesOf('currency').includes(code)
    ? { ok: true, value: code }
    : fail('currency_unknown');
}

export interface ValueInput {
  amount: unknown;
  currency: unknown;
  asOf: unknown;
}

export interface Value {
  /** At most two decimals: a value is money. */
  amount: string;
  currency: string;
  asOf: string;
}

/**
 * **A value**, by hand: an amount of money in one currency on one day, never in the future, at most two decimals.
 * Zero is a value (a card paid off this month); a negative is not. Each is a new row: the history is kept and the
 * newest is shown (F372, question 2).
 */
export function parseValue(input: ValueInput, today: string): Parsed<Value> {
  const amount = parseDecimal(input.amount);
  if (!amount.ok || amount.value === null || (amount.value.split('.')[1] ?? '').length > 2)
    return fail('value_not_a_number');
  const currency = parseCurrency(input.currency);
  if (!currency.ok) return currency;
  const asOf = parseDay(input.asOf);
  if (!asOf.ok) return asOf;
  if (asOf.value === null) return fail('invalid_date');
  if (asOf.value > today) return fail('date_in_future');
  return { ok: true, value: { amount: amount.value, currency: currency.value, asOf: asOf.value } };
}

export interface ValuedRow {
  id: string;
  endedOn: string | null;
  /** The newest value, or null for a row that has none yet. */
  value: Value | null;
}

export interface CurrencyTotal {
  currency: string;
  assets: string;
  liabilities: string;
  net: string;
}

/** Sums of two-decimal amounts in øre, as integers, so a total never drifts by a rounding. */
const minor = (amount: string) => {
  const [whole = '0', fraction = ''] = amount.split('.');
  return BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
};
const major = (value: bigint) => {
  const sign = value < 0n ? '-' : '';
  const abs = value < 0n ? -value : value;
  return `${sign}${abs / 100n}.${String(abs % 100n).padStart(2, '0')}`;
};

/**
 * **The totals on Finances**: for each currency, what the live assets are worth by their newest value, what the
 * live liabilities stand at, and the difference. One line per currency, NOK first, then by code: a total across
 * currencies would be a conversion, and none is made. An ended row and one with no value yet count for nothing.
 */
export function holdingsTotals(
  assets: readonly ValuedRow[],
  liabilities: readonly ValuedRow[],
): CurrencyTotal[] {
  const sums = new Map<string, { assets: bigint; liabilities: bigint }>();
  const add = (rows: readonly ValuedRow[], side: 'assets' | 'liabilities') => {
    for (const row of rows) {
      if (row.endedOn !== null || row.value === null) continue;
      const sum = sums.get(row.value.currency) ?? { assets: 0n, liabilities: 0n };
      sum[side] += minor(row.value.amount);
      sums.set(row.value.currency, sum);
    }
  };
  add(assets, 'assets');
  add(liabilities, 'liabilities');
  return [...sums.entries()]
    .sort(([a], [b]) => Number(a !== 'NOK') - Number(b !== 'NOK') || a.localeCompare(b))
    .map(([currency, sum]) => ({
      currency,
      assets: major(sum.assets),
      liabilities: major(sum.liabilities),
      net: major(sum.assets - sum.liabilities),
    }));
}

export interface CurrencyTotalWithBank extends CurrencyTotal {
  /** What the person's own bank accounts hold in this currency, read just now. */
  bank: string;
}

/**
 * **The totals with the bank beside them** (Personal build step 5): each currency's typed assets and liabilities, what
 * the connected accounts hold by their headline balance, and the net of all three. An overdrawn account counts against
 * the net, as it is. Still one line per currency and nothing converted; a currency only the bank has gets a line.
 */
export function totalsWithBank(
  totals: readonly CurrencyTotal[],
  balances: readonly { currency: string; amount: string }[],
): CurrencyTotalWithBank[] {
  const bank = new Map<string, bigint>();
  for (const b of balances) {
    const negative = b.amount.startsWith('-');
    const value = minor(negative ? b.amount.slice(1) : b.amount);
    bank.set(b.currency, (bank.get(b.currency) ?? 0n) + (negative ? -value : value));
  }
  const rows = new Map(totals.map((t) => [t.currency, t]));
  for (const currency of bank.keys())
    if (!rows.has(currency))
      rows.set(currency, { currency, assets: '0.00', liabilities: '0.00', net: '0.00' });
  return [...rows.values()]
    .sort(
      (a, b) =>
        Number(a.currency !== 'NOK') - Number(b.currency !== 'NOK') ||
        a.currency.localeCompare(b.currency),
    )
    .map((t) => {
      const held = bank.get(t.currency) ?? 0n;
      return { ...t, bank: major(held), net: major(minor(t.assets) + held - minor(t.liabilities)) };
    });
}
